import type { ControlSize, Tone } from './types'

/**
 * Triedy zdieľané medzi primitívmi.
 *
 * Sú tu, a nie v každom komponente, lebo výška ovládacieho prvku a stav
 * „neplatné pole“ musia vyzerať rovnako v Input, Select, Combobox aj
 * NumberInput — inak sa formulár rozpadne na tri rôzne vysoké riadky.
 */

/** Spojí triedy a zahodí prázdne vetvy; `clsx` kvôli tomuto netreba. */
export function cn(...values: (string | false | null | undefined)[]): string {
	return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

/** Výšky presne podľa handoff.md — 28 / 36 / 40 px, vždy z tokenu. */
export const CONTROL_HEIGHT: Readonly<Record<ControlSize, string>> = {
	sm: 'h-(--control-h-sm) text-sm',
	md: 'h-(--control-h) text-base',
	lg: 'h-(--control-h-lg) text-md',
}

/** Vodorovné odsadenie rastie s výškou, aby prvok nepôsobil stlačene. */
export const CONTROL_PADDING: Readonly<Record<ControlSize, string>> = {
	sm: 'px-2',
	md: 'px-3',
	lg: 'px-3',
}

/** Základ textového vstupu: 1px border, radius 6, focus ring z tokenu. */
export const INPUT_BASE =
	'w-full min-w-0 rounded-md border border-border bg-surface text-fg placeholder:text-fg-subtle ' +
	'transition-colors duration-(--motion-fast) ease-out ' +
	'hover:border-border-strong focus-visible:focus-ring focus-visible:border-brand ' +
	'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-fg-subtle'

/** Chybný stav je červený rám aj bez fokusu — chyba nesmie byť len v texte. */
export const INPUT_INVALID = 'border-danger hover:border-danger'

/** Farby stavových plôch (badge, hlásenie). Brand medzi nimi zámerne nie je. */
export const TONE_SURFACE: Readonly<Record<Tone, string>> = {
	neutral: 'bg-surface-muted text-fg-muted border-border',
	brand: 'bg-brand-soft text-fg border-brand',
	success: 'bg-success-bg text-success-fg border-success-bg',
	warning: 'bg-warning-bg text-warning-fg border-warning-bg',
	danger: 'bg-danger-bg text-danger-fg border-danger-bg',
	info: 'bg-info-bg text-info-fg border-info-bg',
}

/** Ikony sú výhradne Lucide, stroke 1.75, veľkosti 16 / 20 / 24 (MASTER.md §5). */
export const ICON_STROKE = 1.75
export const ICON_SIZE: Readonly<Record<ControlSize, number>> = { sm: 16, md: 16, lg: 20 }
